import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { BootstrapProvider } from "./providers/BootstrapProvider";
import { BrandCssInjector } from "./components/BrandCssInjector";
import InvisibleWatermark from "./components/InvisibleWatermark";

// Pages
import LandingPage from "./pages/LandingPage";
import PausedScreen from "./pages/PausedScreen";
import PrelaunchLanding from "./pages/PrelaunchLanding";
import BetaRedeem from "./pages/BetaRedeem";
import BetaLaunchLanding from "./pages/BetaLaunchLanding";
import LoginPage from "./pages/LoginPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import { DraftRestorer } from "./components/DraftRestorer";
import { snapshotDrafts } from "./lib/draftKeeper";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CMSLegalPage from "./pages/CMSLegalPage";
import BrandLegalPage from "./pages/BrandLegalPage";
import CommunityGuidelines from "./pages/CommunityGuidelines";
import Dashboard from "./pages/Dashboard";
import DiscussionsPage from "./pages/DiscussionsPage";
import DiscussionDetailPage from "./pages/DiscussionDetailPage";
import MessagesPage from "./pages/MessagesPage";
import ResourcesPage from "./pages/ResourcesPage";
import VideoRoomsPage from "./pages/VideoRoomsPage";
import VideoCallPage from "./pages/VideoCallPage";
import ProfilePage from "./pages/ProfilePage";
import VerificationPage from "./pages/VerificationPage";
import MembersPage from "./pages/MembersPage";
import BoxBreathingPage from "./pages/BoxBreathingPage";
import AdminPage from "./pages/AdminPage";
import FounderDashboard from "./pages/FounderDashboard";
import TierManagementPage from "./pages/TierManagementPage";
import FAQManagementPage from "./pages/FAQManagementPage";
import ContentManagementPage from "./pages/ContentManagementPage";
import EventsPage from "./pages/EventsPage";
import CETrackerPage from "./pages/CETrackerPage";
import MembershipPage from "./pages/MembershipPage";
import ShiftTrackerPage from "./pages/ShiftTrackerPage";
import HealthTrackerPage from "./pages/HealthTrackerPage";
import FinancePage from "./pages/FinancePage";
import MileagePage from "./pages/MileagePage";
import JobBoardPage from "./pages/JobBoardPage";
import ClinicRatingsPage from "./pages/ClinicRatingsPage";
import PartnersPage from "./pages/PartnersPage";
import WageTransparencyPage from "./pages/WageTransparencyPage";
import NewGradPage from "./pages/NewGradPage";
import CommunityPage from "./pages/CommunityPage";
import MentorshipPage from "./pages/MentorshipPage";
import GroupsPage from "./pages/GroupsPage";
import MeetupsPage from "./pages/MeetupsPage";
import NotesPage from "./pages/NotesPage";
import MembershipChoicePage from "./pages/MembershipChoicePage";
import PollsPage from "./pages/PollsPage";
import HygienaChat from "./components/HygienaChat";
import { useHygienaEnabled } from "./lib/hygienaStatus";
import PolicyAcceptanceModal from "./components/PolicyAcceptanceModal";

// Resolve the API base URL.
//
// In dev/preview, REACT_APP_BACKEND_URL is the preview hostname which matches
// the page's own origin → axios calls are same-origin, no CORS, cookies work.
//
// In production with a custom domain (e.g. dhdh.ca), the build embeds the
// original Emergent host (e.g. dental-connect-pro.emergent.host). Calling that
// from dhdh.ca is cross-origin and iOS Safari's ITP blocks the cookies, which
// shows up to the user as "Network Error" on login. Detection: if the configured
// BACKEND_URL doesn't match the page origin AND we're not on localhost, fall
// back to the page's own origin — both Emergent and the custom domain route
// /api/* to the same backend pod, so this is safe.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const isCustomDomainMismatch = (() => {
  if (!ENV_BACKEND_URL || !ORIGIN) return false;
  if (ORIGIN.startsWith("http://localhost")) return false;
  try {
    return new URL(ENV_BACKEND_URL).hostname !== new URL(ORIGIN).hostname;
  } catch {
    return false;
  }
})();
const BACKEND_URL = isCustomDomainMismatch ? ORIGIN : ENV_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Pretty labels for tier upgrade prompts (matches PLAN_FEATURES min_tier values)
const TIER_LABEL = { student: "Student & New Grad", community: "The Community", hygienist: "The Hygienist", pro: "RDH Pro" };
const FEATURE_LABEL = {
  ce_tracker: "CE Tracker", mileage_tracker: "Mileage Tracker",
  shift_tracker: "Shift Tracker", finance_tracker: "Receipt & Expense Tracker",
  health_tracker: "Health Tracker", breathing: "Box Breathing Suite",
  clinic_ratings_read: "Clinic Ratings", clinic_ratings_submit: "Submitting Clinic Ratings",
  job_board: "Job Board", partners_directory: "Preferred Partners",
  groups_create: "Creating Groups", mentorship_matching: "Mentorship Matching",
  messages: "Private Messaging", meetups: "Local Meetups",
  events: "Professional Events", resources: "New Grad / Resources Hub",
  wage_surveys: "Wage Survey", discussions: "Anonymous Discussion Board",
  topic_of_month: "Topic of the Month",
  notes_basic: "Note Templates", notes_full: "Note Templates — Full Library",
  hygiena_ai: "Hygiena AI Assistant",
};

// Global 402 PAYMENT_REQUIRED handler — backend emits "UPGRADE_REQUIRED:<feature>:<tier>"
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const reqUrl = error?.config?.url || "";
      // Session expired mid-use: keep whatever the member was typing, then send them to sign in.
      const sessionGone = typeof detail === "string" && /session expired|token expired|not authenticated|invalid token|user not found/i.test(detail);
      if (status === 401 && sessionGone && window.__dhdh_authed && !/\/auth\/(login|logout|verify-email|change-password|reset-password)/.test(reqUrl)) {
        window.__dhdh_authed = false;
        const saved = snapshotDrafts(error.config);
        sessionStorage.setItem("dhdh_return_to", JSON.stringify({ path: window.location.pathname + window.location.search, uid: window.__dhdh_uid || null }));
        window.dispatchEvent(new CustomEvent("dhdh:session-expired", { detail: { saved } }));
      }
      if (status === 402 && typeof detail === "string" && detail.startsWith("UPGRADE_REQUIRED:")) {
        const [, featureKey, minTier] = detail.split(":");
        const featureName = FEATURE_LABEL[featureKey] || featureKey.replace(/_/g, " ");
        const tierName = TIER_LABEL[minTier] || minTier;
        // Avoid spamming if multiple gated calls fire at once
        if (!window.__dhdh_upgrade_toast_at || Date.now() - window.__dhdh_upgrade_toast_at > 3000) {
          window.__dhdh_upgrade_toast_at = Date.now();
          import("sonner").then(({ toast }) => {
            toast.error(`${featureName} requires ${tierName}`, {
              description: "Upgrade your tier to unlock this feature.",
              action: {
                label: "Upgrade",
                onClick: () => { window.location.href = "/membership"; },
              },
              duration: 6000,
            });
          });
        }
      }
    } catch { /* never let interceptor break the response chain */ }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Helper to format API error detail
export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.__dhdh_authed = !!user; if (user) window.__dhdh_uid = user.user_id; }, [user]);
  useEffect(() => {
    const onExpired = (e) => {
      setUser(null);
      import("sonner").then(({ toast }) => toast.warning("Your session expired — please sign in again.", {
        description: e.detail?.saved ? "We saved what you were writing and will put it back after you sign in." : undefined,
        duration: 8000,
      }));
    };
    window.addEventListener("dhdh:session-expired", onExpired);
    return () => window.removeEventListener("dhdh:session-expired", onExpired);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    if (!response.data?.verification_required) setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  // Sprint 6 (Phase 62): re-fetch the current user on window focus so tier
  // changes by the founder are reflected on the active tab within seconds
  // without forcing a logout/refresh. Only fires when we believe a user is
  // logged in (no point hammering /me when on the login screen) and we
  // throttle to once every 10 seconds to be polite to the API.
  useEffect(() => {
    if (!user) return;
    let lastFocusAt = 0;
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusAt < 10000) return;
      lastFocusAt = now;
      refreshUser();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Pending-member whitelist: while verification_status !== 'verified', user can only
// reach these few routes. Everything else bounces to /dashboard so they see the
// locked overlay + "Awaiting admin approval" copy.
const PENDING_MEMBER_PATHS = ["/dashboard", "/breathing", "/verification", "/profile", "/logout"];
const isAllowedForPendingMember = (pathname) =>
  PENDING_MEMBER_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

// Protected Route component
// Routes pass `requireVerified={false}` to allow non-verified members (Dashboard, Profile, Verification, Membership choice)
const ProtectedRoute = ({ children, requireVerified = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Passwords that don't meet policy (or admin-issued temp passwords) must be changed first.
  if (user.must_change_password && location.pathname !== "/profile") {
    return <Navigate to="/profile?tab=password" replace />;
  }

  // Admins bypass all gates
  if (user.is_admin) return children;

  // Guest Professional accounts (founder-created consultant accounts for
  // lawyers, accountants, etc.) bypass the verification/trial/payment
  // gates entirely. They are READ-ONLY platform viewers; write actions
  // are blocked at the backend `require_verified_member` gate.
  const isGuestProfessional = user.member_type === "guest_professional" || user.is_guest_professional;
  if (isGuestProfessional) {
    if (requireVerified && user.verification_status !== "verified") {
      // Allow them through the verification gate — they're approved by founder.
      return children;
    }
    return children;
  }

  // Pending-approval users (just registered, awaiting admin verification) get a
  // tightly-scoped experience: dashboard, box breathing, profile, verification only.
  // Their 30-day trial clock hasn't started yet — it begins the moment admin approves.
  const isPending = user.verification_status !== "verified" || user.membership_status === "pending_approval";
  if (isPending) {
    if (!isAllowedForPendingMember(location.pathname)) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  }

  // Step 1: must pick a membership (4-tier pricing page handles both new picks and upgrades)
  if (user.requires_choice && location.pathname !== "/membership" && location.pathname !== "/membership/success") {
    return <Navigate to="/membership" replace />;
  }

  // Phase 64.G / C-2: paused members get read-only access.
  // Allowed: dashboard, membership, account (export), legal pages, profile.
  // Everything else bounces to /paused.
  if (user.is_paused) {
    const allowed = [
      "/dashboard", "/membership", "/membership/success", "/profile",
      "/account", "/account/export",
      "/privacy", "/terms", "/community-guidelines",
      "/paused",
    ];
    if (!allowed.some(p => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      return <Navigate to="/paused" replace />;
    }
    return children;
  }

  // Step 2: trial expired / no active membership → must upgrade
  if (user.requires_upgrade && location.pathname !== "/membership" && location.pathname !== "/membership/success") {
    return <Navigate to="/membership" replace />;
  }

  // Step 3: hard verification gate (defense-in-depth — should be unreachable now since pending check above)
  if (requireVerified && user.verification_status !== "verified") {
    return <Navigate to="/verification" replace />;
  }

  return children;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  if (user.must_change_password) {
    return <Navigate to="/profile?tab=password" replace />;
  }

  return children;
};

// Founder Route component (founder OR admin can access)
const FounderRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isFounderOrAdmin =
    user?.admin_role === "founder" ||
    user?.role === "founder" ||
    user?.is_admin;

  if (!user || !isFounderOrAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  if (user.must_change_password) {
    return <Navigate to="/profile?tab=password" replace />;
  }

  return children;
};

// ---- Launch Mode Gate ----
// During prelaunch, the only public-visible URLs are: /, /beta, /beta/:code,
// /privacy, /terms. Everything else redirects home until the visitor has either
// (a) redeemed a beta invite cookie, or (b) authenticated as an admin.
const LaunchModeContext = createContext({ mode: "open", isPrelaunch: false });
export const useLaunchMode = () => useContext(LaunchModeContext);

const LaunchModeProvider = ({ children }) => {
  // SECURITY: Default to prelaunch=true / loaded=false. We assume the gate is ON
  // until we hear otherwise from the server. This prevents any flash of full-app
  // content to a visitor who shouldn't see it (e.g. on slow connections, blocked
  // JS, failed API call). The server's actual LAUNCH_MODE is the single source
  // of truth — the frontend only ever opens the gate after a successful response.
  const [state, setState] = useState({
    mode: "prelaunch",
    isPrelaunch: true,
    hasBetaAccess: false,
    loaded: false,
  });
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/launch/mode`).then(r => {
      if (cancelled) return;
      setState({
        mode: r.data.mode,
        isPrelaunch: r.data.is_prelaunch,
        hasBetaAccess: r.data.has_beta_access,
        loaded: true,
      });
    }).catch(() => {
      // On API failure, FAIL CLOSED — keep the gate up. Better to lock everyone
      // out for a moment than to leak the full platform.
      if (cancelled) return;
      setState({ mode: "prelaunch", isPrelaunch: true, hasBetaAccess: false, loaded: true });
    });
    return () => { cancelled = true; };
  }, []);
  return <LaunchModeContext.Provider value={state}>{children}</LaunchModeContext.Provider>;
};

// During prelaunch only these paths are reachable to non-admins:
//  - "/"        (waitlist landing — name + email signup)
//  - "/beta"    (existing beta-code redemption)
//  - "/login"   (direct URL — not linked from prelaunch landing; admins/founders need it)
//  - "/privacy" / "/terms" (legal pages — required to be public)
// Everything else (including /register) bounces to the prelaunch landing.
const PRELAUNCH_PUBLIC_PATHS = ["/", "/beta", "/login", "/privacy", "/terms", "/cookie-policy", "/disclaimer", "/community-guidelines", "/forgot-password", "/reset-password", "/verify-email"];
const isAllowedDuringPrelaunch = (pathname) =>
  PRELAUNCH_PUBLIC_PATHS.includes(pathname) ||
  pathname.startsWith("/beta/") ||
  pathname.startsWith("/privacy/") ||
  pathname.startsWith("/terms/");

const PrelaunchGate = ({ children }) => {
  const { isPrelaunch, loaded, hasBetaAccess } = useLaunchMode();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // While we don't know yet, show a spinner (NOT the full app shell).
  // The provider defaults to isPrelaunch=true so the SPA never flashes children
  // before we get a server response.
  if (!loaded || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  // Open mode (post-launch) — no gating.
  if (!isPrelaunch) return children;
  // Admins / founders bypass the gate so they can keep working.
  if (user?.is_admin) return children;
  // Beta cookie holders bypass the gate.
  if (hasBetaAccess) return children;
  // Verified members (their session is valid even if no beta cookie) bypass the gate.
  if (user) return children;
  // Otherwise: this is an un-credentialed visitor during prelaunch. Render the
  // PrelaunchLanding directly EXCEPT for explicitly-allowed public paths
  // (/beta, /login, /privacy, /terms, /community-guidelines, and / itself).
  // Rendering directly (instead of <Navigate to="/">) avoids a redirect race
  // that could briefly flash other content.
  if (isAllowedDuringPrelaunch(location.pathname)) return children;
  return <PrelaunchLanding />;
};

// Conditional landing — coming-soon during prelaunch unless visitor has beta access
const RootLanding = () => {
  const { isPrelaunch, loaded } = useLaunchMode();
  const { loading, user } = useAuth();
  const location = useLocation();
  // Founder preview: when ramneetrdh@gmail.com (or any user with admin_role=founder)
  // opens dhdh.ca/?preview=launch, render the post-launch LandingPage even while
  // the platform is still in prelaunch mode. Lets the founder review the live
  // public-launch experience on the real dhdh.ca domain without flipping LAUNCH_MODE.
  const wantsLaunchPreview = new URLSearchParams(location.search).get("preview") === "launch";
  const isFounder = user && (user.email === "ramneetrdh@gmail.com" || user.admin_role === "founder");
  if (!loaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="w-12 h-12 border-4 border-[#6B4E8A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  // Founder preview override — always renders LandingPage on `/`.
  if (wantsLaunchPreview && isFounder) return <LandingPage />;
  // Logged-in members go to their dashboard.
  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;
  // While in prelaunch, EVERYONE (including beta-cookie holders) sees the
  // prelaunch waitlist landing on "/". This is intentional — the waitlist /
  // contact / partner-interest forms must stay reachable on the root URL even
  // for someone who has already redeemed a beta code. Beta testers reach the
  // launch landing explicitly via /beta-launch (after redemption flow).
  // Admins/founders also see the prelaunch landing here so they don't get the
  // "I'm seeing the live site!" confusion when opening dhdh.ca in a tab —
  // they reach the app directly via /dashboard or /admin (or use ?preview=launch).
  if (isPrelaunch) return <PrelaunchLanding />;
  return <LandingPage />;
};

// App Router
function AppRouter() {
  return (
    <PrelaunchGate>
      <Routes>
        <Route path="/" element={<RootLanding />} />
        <Route path="/beta" element={<BetaRedeem />} />
        <Route path="/beta/:code" element={<BetaRedeem />} />
        <Route path="/beta-launch" element={<BetaLaunchLanding />} />
        {/* Founder-only direct preview routes — render LandingPage + BetaLaunchLanding
            without requiring beta-cookie or LAUNCH_MODE flip. Useful for previewing
            launch-day copy/design from inside the app. */}
        <Route path="/preview/launch" element={<LandingPage />} />
        <Route path="/preview/beta-launch" element={<BetaLaunchLanding />} />
        <Route path="/privacy" element={<CMSLegalPage slug="privacy" />} />
        <Route path="/terms" element={<CMSLegalPage slug="terms" />} />
        <Route path="/cookie-policy" element={<CMSLegalPage slug="cookie" />} />
        <Route path="/disclaimer" element={<CMSLegalPage slug="disclaimer" />} />
        <Route path="/community-guidelines" element={<CommunityGuidelines />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute requireVerified={false}><Dashboard /></ProtectedRoute>} />
      <Route path="/discussions" element={<ProtectedRoute><DiscussionsPage /></ProtectedRoute>} />
      <Route path="/discussions/:id" element={<ProtectedRoute><DiscussionDetailPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/messages/:userId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><ResourcesPage /></ProtectedRoute>} />
      <Route path="/video" element={<ProtectedRoute><VideoRoomsPage /></ProtectedRoute>} />
      <Route path="/video/:roomId" element={<ProtectedRoute><VideoCallPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute requireVerified={false}><ProfilePage /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/verification" element={<ProtectedRoute requireVerified={false}><VerificationPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
      <Route path="/breathing" element={<ProtectedRoute><BoxBreathingPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/ce-tracker" element={<ProtectedRoute><CETrackerPage /></ProtectedRoute>} />
      <Route path="/membership" element={<ProtectedRoute requireVerified={false}><MembershipPage /></ProtectedRoute>} />
      <Route path="/paused" element={<ProtectedRoute requireVerified={false}><PausedScreen /></ProtectedRoute>} />
      <Route path="/membership/choose" element={<ProtectedRoute requireVerified={false}><MembershipChoicePage /></ProtectedRoute>} />
      <Route path="/membership/success" element={<ProtectedRoute requireVerified={false}><MembershipPage /></ProtectedRoute>} />
      <Route path="/shifts" element={<ProtectedRoute><ShiftTrackerPage /></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute><HealthTrackerPage /></ProtectedRoute>} />
      <Route path="/finance" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
      <Route path="/mileage" element={<ProtectedRoute><MileagePage /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobBoardPage /></ProtectedRoute>} />
      <Route path="/clinic-ratings" element={<ProtectedRoute><ClinicRatingsPage /></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><PartnersPage /></ProtectedRoute>} />
      <Route path="/wages" element={<ProtectedRoute><WageTransparencyPage /></ProtectedRoute>} />
      <Route path="/new-grads" element={<ProtectedRoute><NewGradPage /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
      <Route path="/mentorship" element={<ProtectedRoute><MentorshipPage /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/meetups" element={<ProtectedRoute><MeetupsPage /></ProtectedRoute>} />
      <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
      <Route path="/polls" element={<ProtectedRoute><PollsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/admin/tiers" element={<AdminRoute><TierManagementPage /></AdminRoute>} />
      <Route path="/admin/faq" element={<AdminRoute><FAQManagementPage /></AdminRoute>} />
      <Route path="/admin/content" element={<AdminRoute><ContentManagementPage /></AdminRoute>} />
      <Route path="/admin/brand-legal" element={<AdminRoute><BrandLegalPage /></AdminRoute>} />
      <Route path="/founder" element={<FounderRoute><FounderDashboard /></FounderRoute>} />
      <Route path="/privacy" element={<CMSLegalPage slug="privacy" />} />
      <Route path="/terms" element={<CMSLegalPage slug="terms" />} />
      <Route path="/cookie-policy" element={<CMSLegalPage slug="cookie" />} />
      <Route path="/disclaimer" element={<CMSLegalPage slug="disclaimer" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </PrelaunchGate>
  );
}

// Wrapper that shows HygienaChat on authenticated pages and gates new beta testers
// behind a one-time Privacy / Terms / Community-Guidelines acceptance modal.
const AuthenticatedLayout = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  // Don't trap users while they're trying to READ the policies they're being asked
  // to accept. Also let admins/founders / specific exempt accounts straight through.
  const onLegalPage = ["/privacy", "/terms", "/cookie-policy", "/disclaimer", "/community-guidelines"].some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  const email = (user?.email || "").toLowerCase();
  const popupExempt =
    !user ||
    user.is_admin ||
    user.role === "founder" ||
    user.admin_role === "founder" ||
    email === "ramneetrdh@gmail.com" ||
    email === "admin@dhdh.ca";

  // Phase 72: ask the server whether this user needs to re-accept any
  // ENABLED document at its current version. Avoids stale client state.
  useEffect(() => {
    if (!user || popupExempt) { setNeedsAcceptance(false); return; }
    let cancelled = false;
    axios
      .get(`${API}/agreement-popup/needs-acceptance`)
      .then((r) => { if (!cancelled) setNeedsAcceptance(!!r.data?.needs_acceptance); })
      .catch(() => { if (!cancelled) setNeedsAcceptance(false); });
    return () => { cancelled = true; };
  }, [user?.user_id, popupExempt, user]);
  // HygienaChat (AI assistant) is now ADMIN-ONLY (Phase 53). Regular members,
  // pending members, and the public never see it. The chat still renders for
  // founder + admin so they can use it as an admin assistant from any page;
  // the dedicated "Hygiena AI Admin" tab in the dashboard is the primary
  // surface for password resets / content scans / cross-references.
  const PUBLIC_PATHS = ["/", "/login", "/register", "/forgot-password", "/reset-password", "/verify-email", "/privacy", "/terms", "/cookie-policy", "/disclaimer", "/community-guidelines"];
  const onPublicPath = PUBLIC_PATHS.includes(location.pathname) || location.pathname.startsWith("/reset-password/");
  const isAdminUser =
    !!user && (user.is_admin || user.role === "founder");
  const hygienaOn = useHygienaEnabled(isAdminUser);
  const showChat = isAdminUser && !onPublicPath && hygienaOn;
  return (
    <>
      {children}
      <InvisibleWatermark />
      {showChat && <HygienaChat />}
      {needsAcceptance && !onLegalPage && <PolicyAcceptanceModal onAccepted={() => { setNeedsAcceptance(false); refreshUser(); }} />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BootstrapProvider apiBase={API}>
          <BrandCssInjector />
          <AuthProvider>
            <LaunchModeProvider>
              <Toaster position="top-right" richColors />
              <DraftRestorer />
              <AuthenticatedLayout>
                <AppRouter />
              </AuthenticatedLayout>
            </LaunchModeProvider>
          </AuthProvider>
        </BootstrapProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
